import { useEffect, useState } from 'react';
import { QrCode, Loader2, Trash2, Plus } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import EstadoVacio from '../ui/EstadoVacio';
import CodigoQR from '../ui/CodigoQR';

/* =====================================================================
   Etiquetas QR de activos · DIRECTRIZ-TICKETS F2
   =====================================================================
   El encargado da de alta la etiqueta de un activo o lugar —una impresora,
   una puerta, un montacargas— y TEGO le da su QR para imprimir y pegar. Al
   escanearla, cualquiera abre el reporte con el activo ya puesto, incluso
   sin cuenta (R-TK4). Es de quien atiende tickets.
   ===================================================================== */
function urlDelReporte(token) {
  const origen = (typeof window !== 'undefined' && window.location.origin) || '';
  return `${origen}/reportar/${token}`;
}

export default function EtiquetasActivos() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [nueva, setNueva] = useState('');
  const [creando, setCreando] = useState(false);

  function recargar() {
    api.get('/tickets/qr')
      .then((l) => { setLista(l); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudieron cargar las etiquetas.'));
  }
  useEffect(recargar, []);

  async function crear() {
    if (!nueva.trim() || creando) return;
    setCreando(true);
    try {
      await api.post('/tickets/qr', { etiqueta: nueva.trim() });
      setNueva('');
      recargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo crear la etiqueta.');
    } finally { setCreando(false); }
  }

  async function darDeBaja(id) {
    try { await api.post(`/tickets/qr/${id}/baja`, {}); recargar(); }
    catch (e) { setError(e instanceof ErrorApi ? e.message : 'No se pudo dar de baja.'); }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Etiquetas QR de activos</h1>
        <p className="text-gray-600">Imprime una etiqueta, pégala en el equipo o el lugar, y quien la escanee podrá reportar un problema al instante.</p>
        <Ayuda id="etiquetas_qr.pantalla" />
      </header>

      <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="nueva-etiqueta" className="block text-sm font-medium text-gray-900">
            Nombre del activo o lugar
          </label>
          <Ayuda id="etiquetas_qr.nombre" />
          <input
            id="nueva-etiqueta"
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            maxLength={120}
            placeholder="Por ejemplo: Impresora recepción"
            className="mt-1 w-full rounded-xl border border-gray-300 p-3 text-base
                       focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
          />
        </div>
        <button
          type="button"
          onClick={crear}
          disabled={!nueva.trim() || creando}
          aria-describedby={!nueva.trim() ? 'motivo-etiqueta-nueva' : undefined}
          className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl
                     bg-[var(--color-marca)] px-4 font-semibold text-white
                     disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-5 w-5" aria-hidden="true" /> Crear etiqueta
        </button>
      </div>
      {/* P13 (29 sep 2026): el censo lo contaba «gris sin decir por qué». */}
      <Motivo id="motivo-etiqueta-nueva" cuando={!nueva.trim()} className="-mt-3">
        Escribe el nombre del activo o lugar para poder crear su etiqueta.
      </Motivo>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {lista === null && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando las etiquetas…
        </p>
      )}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={QrCode}
          titulo="Aún no hay etiquetas"
          descripcion="Crea la primera arriba: dale un nombre al activo o lugar y TEGO te dará su QR para imprimir y pegar."
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {lista.map((e) => (
            <li key={e.id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
              <CodigoQR valor={urlDelReporte(e.token)} tamano={96} etiqueta={e.etiqueta} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900">{e.etiqueta}</p>
                <button
                  type="button"
                  onClick={() => darDeBaja(e.id)}
                  className="mt-2 flex items-center gap-1 text-sm text-red-700 hover:text-red-900"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Dar de baja
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
